/**
 * Entry point. Three very different things can load this file, chosen by a
 * query parameter on the same bundle — no separate routes, so a plain
 * static host with no server-side rewrites (e.g. GitLab Pages) can serve
 * all of them:
 *
 * 1. The normal app, running inside a Teams meeting side panel / stage /
 *    details tab. The default, when neither parameter below is present.
 * 2. The teams-js auth popup opened by `src/roster/auth.ts`'s
 *    `getGraphToken()` — reloaded with `?authPopup=1`, so it runs the MSAL
 *    redirect dance and reports back to the parent instead of mounting the
 *    standup UI (BUILD-SPEC.md §6).
 * 3. Teams' mandatory tab configuration page — loaded with `?config=1` when
 *    someone is adding the app to a meeting (manifest.json's
 *    `configurationUrl`). See src/config/ConfigPage.tsx.
 */
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import * as microsoftTeams from "@microsoft/teams-js";
import { FluentProvider } from "@fluentui/react-components";
import type { ControllerFactory } from "./contract";
import { createControllerFactory } from "./live/session";
import { completeAuthPopup, isAuthPopup } from "./roster/auth";
import { ConfigPage } from "./config/ConfigPage";
import { App, DEFAULT_SETTINGS, loadSettings } from "./ui/App";
import { useTeamsTheme } from "./ui/theme";

function isConfigPage(): boolean {
  return new URLSearchParams(window.location.search).get("config") === "1";
}

function ThemedApp({ controllerFactory }: { controllerFactory: ControllerFactory }) {
  const theme = useTeamsTheme();
  return (
    <FluentProvider theme={theme}>
      <App controllerFactory={controllerFactory} />
    </FluentProvider>
  );
}

/**
 * Builds the real controller factory.
 *
 * `ControllerFactory` takes no arguments, so the settings and the current
 * user's identity are captured here, once, at start-up. The factory itself
 * never rejects: if Live Share is unavailable for any reason it returns the
 * single-machine fallback instead (BUILD-SPEC §5).
 *
 * Identity is attribution only — a display name for "who pressed Next", and
 * a user id for the cosmetic "has the app open" marker. Neither is stored
 * anywhere (BUILD-SPEC hard rule 2).
 */
async function buildControllerFactory(): Promise<ControllerFactory> {
  let displayName = "";
  let userId = "";
  try {
    const context = await microsoftTeams.app.getContext();
    displayName = context.user?.displayName ?? "";
    userId = context.user?.id ?? "";
  } catch {
    // Outside Teams, or context unavailable. Attribution degrades to a blank
    // name and the presence marker to nobody; the standup still runs.
  }

  let settings = DEFAULT_SETTINGS;
  try {
    settings = loadSettings();
  } catch {
    // Unreadable storage — start from the defaults.
  }

  return createControllerFactory({ settings, identity: { displayName, userId } });
}

async function main(): Promise<void> {
  if (isAuthPopup()) {
    await completeAuthPopup();
    return;
  }

  const rootEl = document.getElementById("root");
  if (!rootEl) return;

  if (isConfigPage()) {
    // ConfigPage does its own microsoftTeams.app.initialize() — it has to
    // run standalone, since a "no controller yet" version of the standup
    // app is not a meaningful thing to render behind it.
    createRoot(rootEl).render(
      <StrictMode>
        <ConfigPage />
      </StrictMode>
    );
    return;
  }

  try {
    await microsoftTeams.app.initialize();
  } catch {
    // Outside Teams (e.g. a plain browser tab during development) —
    // teams-js calls elsewhere (getContext, theme, authentication) already
    // degrade gracefully on their own, so the app still renders.
  }

  const controllerFactory = await buildControllerFactory();

  createRoot(rootEl).render(
    <StrictMode>
      <ThemedApp controllerFactory={controllerFactory} />
    </StrictMode>
  );
}

void main();
